"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import {
  REFRAME_FOCUS_EXPERIMENT_LAUNCH_DATE,
  ReframeFocusVariant,
} from "../helpers/reframeFocusExperiment";
import { useProfile } from "./useProfile";

const FLAG_KEY = "reframe-focus-default";

const OVERRIDE_FEATURE_FLAGS_KEY = "$override_feature_flags";
const STORED_PERSON_PROPERTIES_KEY = "$stored_person_properties";

// Reads the "reframe-focus-default" experiment arm (see
// helpers/reframeFocusExperiment.ts), mirroring klap-api's
// resolveReframeFocusVariant so the editor shows the same Focus the shorts
// were generated with:
// - Only non-guest profiles created on/after the launch date evaluate the
//   flag; everyone else (guests in the embed, existing users, logged-out
//   visitors) gets control without touching it.
// - The flag's release conditions match on signed_up_at / is_guest, fed via
//   setPersonPropertiesForFlags so evaluation never waits on person-property
//   ingestion (same values klap-api passes as personProperties).
// - The flag has no experience continuity, and after identify() both sides
//   hash the same profile id, so the arms always agree.
// - Evaluated with send_event: false: klap-api records the exposure when it
//   generates a task's shorts, which is when the default takes effect.
// Toolbar / console overrides win over the eligibility gate (local QA).
// Returns control until the profile and flags have loaded.
export const useReframeFocusVariant = (): ReframeFocusVariant => {
  const posthog = usePostHog();
  const { data: profile } = useProfile();
  const [flagVariant, setFlagVariant] = useState<
    string | boolean | undefined
  >();

  const isEligibleProfile =
    !!profile &&
    !profile.isGuest &&
    !!profile.createdAt &&
    new Date(profile.createdAt) >= REFRAME_FOCUS_EXPERIMENT_LAUNCH_DATE;
  const signedUpAt =
    isEligibleProfile && profile?.createdAt
      ? new Date(profile.createdAt).toISOString()
      : undefined;

  // Guarded against re-sending identical values: setPersonPropertiesForFlags
  // (_, true) reloads flags, and this hook (like usePricingLimitVariant, which
  // sets the same values) is mounted by several components.
  useEffect(() => {
    if (!posthog || !signedUpAt) return;
    const stored = posthog.get_property?.(STORED_PERSON_PROPERTIES_KEY) as
      | Record<string, string>
      | undefined;
    if (stored?.signed_up_at === signedUpAt && stored?.is_guest === "false")
      return;
    posthog.setPersonPropertiesForFlags(
      { signed_up_at: signedUpAt, is_guest: "false" },
      true,
    );
  }, [posthog, signedUpAt]);

  useEffect(() => {
    if (!posthog) return;
    const update = () => {
      const overrides = posthog.get_property?.(OVERRIDE_FEATURE_FLAGS_KEY) as
        | Record<string, string | boolean>
        | undefined;
      const hasToolbarOverride =
        !!overrides &&
        typeof overrides === "object" &&
        Object.prototype.hasOwnProperty.call(overrides, FLAG_KEY);
      setFlagVariant(
        hasToolbarOverride || isEligibleProfile
          ? posthog.getFeatureFlag(FLAG_KEY, { send_event: false })
          : undefined,
      );
    };
    update();
    return posthog.onFeatureFlags(update);
  }, [posthog, isEligibleProfile]);

  return flagVariant === "test" ? "test" : "control";
};
